import { Avatar, AvatarFallback, Button, Card, Chip, toast } from "@heroui/react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getUserById as getUserByIdApi } from "@/api/users";
import { useSession } from "@clerk/react";
import { getUserInitials } from "@/utils";
import { formatDate } from "@/utils/dates";
import { getMyMeetsPastByMember as getMyMeetsPastByMemberApi } from "@/api/meets";
import RoleBadge from "@/components/role-badge";
import {
  ArrowLeft,
  Briefcase,
  CalendarClock,
  ChevronRight,
  Copy,
  Mail,
  Phone,
  Plus,
  UserX,
} from "lucide-react";
import ErrorBoundary from "@/components/error-boundries/error-boundry-comp";

function copyToClipboard(value, label) {
  if (!value) return;
  navigator.clipboard
    .writeText(value)
    .then(() => toast.success(`${label} copied`))
    .catch(() => toast.danger(`Couldn't copy ${label.toLowerCase()}`));
}

const SkeletonValue = ({ className = "h-3.5 w-28" }) => (
  <div className={`rounded-full bg-white dark:bg-white/20 animate-pulse ${className}`} />
);

// @/utils/dates
export function getPastDateRange(monthsAgo = 3) {
  const format = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  const end = new Date();
  const start = new Date();
  start.setMonth(start.getMonth() - monthsAgo);

  return [format(start), format(end)];
}

const ConnectionDetailsSkeleton = () => (
  <div className="flex flex-col gap-8 pb-40 mt-20">
    <div className="size-9 rounded-xl bg-default/30 dark:bg-white/20 animate-pulse" />
    <div className="flex flex-col gap-2">
      {/* Header card */}
      <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
        <Card.Content className="flex flex-row items-center gap-4">
          <div className="size-10 rounded-full bg-white dark:bg-white/20 animate-pulse" />
          <div className="flex flex-col gap-2">
            <SkeletonValue className="h-5 w-36" />
            <SkeletonValue className="h-4 w-20" />
          </div>
        </Card.Content>
      </Card>

      {/* Details card */}
      <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
        <Card.Header className="text-sm font-bold">Connection Details</Card.Header>
        <Card.Content>
          {["Email", "Role"].map((label) => (
            <div key={label} className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="size-8 rounded-full bg-default/30 dark:bg-white/20 animate-pulse" />
                <SkeletonValue />
              </div>
              <SkeletonValue />
            </div>
          ))}
        </Card.Content>
      </Card>

      {/* Past loops card */}
      <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
        <Card.Header className="text-sm font-bold">Past Loops</Card.Header>
        <Card.Content className="flex flex-col gap-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="size-8 rounded-full bg-default/30 dark:bg-white/20 animate-pulse shrink-0" />
              <SkeletonValue className="h-3.5 w-40" />
            </div>
          ))}
        </Card.Content>
      </Card>

      <div className="h-11 w-full rounded-full bg-default/30 dark:bg-white/20 animate-pulse" />
    </div>
  </div>
);

const ConnectionNotFound = ({ onBack }) => (
  <div className="flex flex-col gap-8 pb-40 mt-20">
    <Button isIconOnly className={"size-9 icon-button"} onPress={onBack}>
      <ArrowLeft size={12} className="text-[#5C5C5C] dark:text-white size-4" />
    </Button>

    <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
      <Card.Content className="flex flex-col items-center gap-4 py-10 text-center">
        <div className="size-14 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414]">
          <UserX size={24} />
        </div>

        <div className="flex flex-col gap-1">
          <span className="font-bold text-[#2A2760] dark:text-white text-lg">
            Could not find this connection
          </span>
          <span className="text-sm text-[#5C5C5C] dark:text-[#9896BE]">
            It may have been removed, or the link might be incorrect.
          </span>
        </div>

        <Button variant="primary" className="rounded-full mt-2" onPress={onBack}>
          Back to Network
        </Button>
      </Card.Content>
    </Card>
  </div>
);

const ConnectionDetailsPage = () => {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [pastMeets, setPastMeets] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { session } = useSession();
  const navigate = useNavigate();

  const roleLabel = user?.publicMetadata?.role || "Member";

  useEffect(() => {
    getUserById();
    getMyMeetsPastByMember();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getUserById = async () => {
    const token = await session.getToken();
    getUserByIdApi(id, token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setUser(_resp.data.Record);
        } else {
          setNotFound(true);
        }
      })
      .catch((err) => {
        console.log(err);
        setNotFound(true);
      });
  };

  const getMyMeetsPastByMember = async () => {
    const token = await session.getToken();

    getMyMeetsPastByMemberApi(id, getPastDateRange(3), token)
      .then((_resp) => {
        if (_resp.data.Result === "OK") {
          setPastMeets(_resp.data.Records || []);
        } else {
          setPastMeets([]);
        }
      })
      .catch((_err) => {
        console.log(_err);
        setPastMeets([]);
      });
  };

  if (notFound) return <ConnectionNotFound onBack={() => navigate("/network")} />;

  if (!user) return <ConnectionDetailsSkeleton />;

  const email = user.email_addresses?.[0]?.email_address;
  const phone = user.phone_numbers?.[0]?.phone_number;

  return (
    <ErrorBoundary>
      <div className="flex flex-col gap-8 pb-40 mt-16">
        <Button isIconOnly className={"size-9 icon-button"} onPress={() => navigate(-1)}>
          <ArrowLeft size={12} className="text-[#5C5C5C] dark:text-white size-4" />
        </Button>

        <div className="flex flex-col gap-2">
          <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
            <Card.Content className="flex flex-row items-center gap-4">
              <Avatar color="primary" variant="primary" className="bg-accent text-accent-foreground">
                <AvatarFallback className="bg-accent text-accent-foreground uppercase">
                  {getUserInitials(`${user?.first_name} ${user.last_name}`)}
                </AvatarFallback>
              </Avatar>

              <div className="flex flex-col items-start gap-2">
                <span className="font-bold capitalize text-[#2A2760] dark:text-white text-xl">{`${user?.first_name} ${user.last_name}`}</span>
                <RoleBadge role={roleLabel} />
              </div>
            </Card.Content>
          </Card>

          <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none">
            <Card.Header className="text-sm font-bold">Connection Details</Card.Header>
            <Card.Content>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414]">
                    <Mail size={16} />
                  </div>

                  <span className="text-black dark:text-[#ECECEC] text-sm font-medium">{email}</span>
                </div>

                <button type="button" aria-label="Copy email" onClick={() => copyToClipboard(email, "Email")}>
                  <Copy size={16} className="text-[#9896BE]" />
                </button>
              </div>

              {phone && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="size-8 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414]">
                      <Phone size={16} />
                    </div>

                    <span className="text-black dark:text-[#ECECEC] text-sm font-medium">{phone}</span>
                  </div>

                  <button
                    type="button"
                    aria-label="Copy phone number"
                    onClick={() => copyToClipboard(phone, "Phone number")}
                  >
                    <Copy size={16} className="text-[#9896BE]" />
                  </button>
                </div>
              )}

              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="size-8 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414]">
                    <Briefcase size={16} />
                  </div>

                  <span className="text-black dark:text-[#ECECEC] text-sm">Role</span>
                </div>

                <Chip className=" bg-white">
                  <Chip.Label className="text-accent font-medium text-sm">{roleLabel}</Chip.Label>
                </Chip>
              </div>
            </Card.Content>
          </Card>

          <Card className="bg-[#FFFFFFAD] dark:bg-black shadow-none max-h-70 ">
            <Card.Header className="text-sm font-bold">Past Loops</Card.Header>
            <Card.Content className="flex flex-col gap-3 overflow-y-auto">
              {pastMeets === null && <p className="text-sm text-muted text-center py-4">Loading past loops...</p>}

              {pastMeets !== null && pastMeets.length === 0 && (
                <p className="text-sm text-muted text-center py-4">No past loops in the last 3 months.</p>
              )}

              {pastMeets
                ?.slice()
                .sort((a, b) => b["meeting_date"].localeCompare(a["meeting_date"]))
                .map((meet) => (
                  <button
                    type="button"
                    key={meet["_id"]}
                    onClick={() => navigate(`/meetings/view/${meet["short_code"]}`)}
                    className="flex items-center justify-between gap-3 w-full text-left"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="size-8 rounded-full flex items-center justify-center text-accent bg-white dark:bg-[#141414] shrink-0">
                        <CalendarClock size={16} />
                      </div>
                      <div className="flex flex-col min-w-0">
                        <span className="text-black dark:text-[#ECECEC] text-sm font-medium truncate">
                          {meet["title"] || "Untitled Meeting"}
                        </span>
                        <span className="text-xs text-[#5C5C5C] dark:text-[#9896BE]">
                          {formatDate(meet["meeting_date"])} · {meet["start_time"]?.set}–{meet["end_time"]?.set}
                        </span>
                      </div>
                    </div>
                    <ChevronRight size={16} className="text-[#9896BE] shrink-0" />
                  </button>
                ))}
            </Card.Content>
          </Card>

          <Button
            variant="primary"
            className="w-full rounded-full"
            onPress={() => navigate(`/meetings/new?with=${id}`)}
          >
            Create Loop
            <Plus className="text-white" size={16} />
          </Button>
        </div>
      </div>
    </ErrorBoundary>
  );
};

export default ConnectionDetailsPage;